import React from 'react';
import Modal from 'react-bootstrap/Modal';
import './RunConfigModal.css';

export interface RunConfig {
  run_mode?: string;
  run_name?: string | null;
  base_url?: string | null;
  source_job_id?: number | null;
  source_run_name?: string | null;
  scope?: {
    selected_test_count?: number | null;
    resolved_test_keys?: string[];
    test_keys?: string[];
    test_case_ids?: number[];
    folder_path?: string[];
    category?: string | null;
  };
  data_source?: {
    mode?: string;
    label?: string;
    dataset_ids?: string[];
    dataset_count?: number;
    generated_dataset_id?: string | null;
    total_rows?: number | null;
    matched_rows?: number | null;
    missing_test_count?: number | null;
    num_variants?: number | null;
    datasets?: Array<Record<string, any>>;
    unmatched_behavior?: string | null;
  };
  attachments?: Array<{ filename?: string; description?: string }>;
  attachment_count?: number;
  credentials?: {
    count?: number;
    keys?: string[];
    fields?: Array<{ key: string; masked: boolean; value: string }>;
  };
  additional_info?: {
    provided?: boolean;
    preview?: string;
  };
}

interface RunConfigModalProps {
  show: boolean;
  onHide: () => void;
  runConfig?: RunConfig | null;
  fallbackBaseUrl?: string | null;
  runName?: string | null;
  jobId?: number | string | null;
}

type ConfigRow =
  | { section: string }
  | { label: string; value: React.ReactNode; mono?: boolean };

function labelize(value?: string | null): string {
  if (!value) return '—';
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
}

function joinOrDash(values?: Array<string | number> | null, limit = 6): string {
  const clean = (values || []).filter(v => v !== null && v !== undefined && String(v).trim().length > 0);
  if (clean.length === 0) return '—';
  const shown = clean.slice(0, limit).map(String).join(', ');
  return clean.length > limit ? `${shown} +${clean.length - limit} more` : shown;
}

function valueOrDash(value: React.ReactNode): React.ReactNode {
  if (value === null || value === undefined || value === '') return '—';
  return value;
}

function scopeDisplay(scope?: RunConfig['scope']): string {
  if (!scope) return '—';
  if (scope.category) return scope.category;
  if ((scope.folder_path || []).length > 0) return joinOrDash(scope.folder_path);
  if ((scope.test_keys || []).length > 0) return joinOrDash(scope.test_keys);
  if ((scope.resolved_test_keys || []).length > 0) return joinOrDash(scope.resolved_test_keys);
  return '—';
}

function attachmentDisplay(attachments?: RunConfig['attachments']): string {
  const values = (attachments || []).map(item => {
    const filename = item.filename || 'attachment';
    return item.description ? `${filename} (${item.description})` : filename;
  });
  return joinOrDash(values);
}

function sourceRunDisplay(config: RunConfig): string {
  const sourceName = config.source_run_name?.trim();
  if (sourceName && config.source_job_id) return `${sourceName} (#${config.source_job_id})`;
  if (sourceName) return sourceName;
  if (config.source_job_id) return `#${config.source_job_id}`;
  return '—';
}

function sourceRunBadge(config?: RunConfig | null): string | null {
  if (!config?.source_job_id && !config?.source_run_name) return null;
  const sourceName = config.source_run_name?.trim();
  if (sourceName && config.source_job_id) return `Source: ${sourceName} (#${config.source_job_id})`;
  if (sourceName) return `Source: ${sourceName}`;
  return `Source job #${config.source_job_id}`;
}

function buildRows(config: RunConfig, fallbackBaseUrl?: string | null, runName?: string | null): ConfigRow[] {
  const scope = config.scope || {};
  const dataSource = config.data_source || {};
  const credentialKeys = config.credentials?.keys || config.credentials?.fields?.map(field => field.key) || [];
  const selectedCount =
    scope.selected_test_count ??
    scope.resolved_test_keys?.length ??
    scope.test_keys?.length ??
    null;

  return [
    { section: 'Run' },
    { label: 'Mode', value: labelize(config.run_mode) },
    { label: 'Run Name', value: valueOrDash(config.run_name || runName) },
    { label: 'Target URL', value: valueOrDash(config.base_url || fallbackBaseUrl), mono: true },
    { label: 'Source Run', value: sourceRunDisplay(config) },

    { section: 'Scope' },
    { label: 'Selected Tests', value: selectedCount ?? '—' },
    { label: 'Scope', value: scopeDisplay(scope) },
    { label: 'Resolved Test Keys', value: joinOrDash(scope.resolved_test_keys) },

    { section: 'Data Source' },
    { label: 'Type', value: dataSource.label || labelize(dataSource.mode) },
    { label: 'Dataset Count', value: dataSource.dataset_count ?? '—' },
    { label: 'Dataset IDs', value: joinOrDash(dataSource.dataset_ids) },
    { label: 'Generated Dataset', value: valueOrDash(dataSource.generated_dataset_id) },
    { label: 'Synthetic Rows', value: dataSource.matched_rows ?? dataSource.total_rows ?? '—' },
    { label: 'Variants', value: dataSource.num_variants ?? '—' },
    { label: 'Missing Tests', value: dataSource.missing_test_count ?? '—' },

    { section: 'Inputs' },
    { label: 'Credential Keys', value: joinOrDash(credentialKeys) },
    { label: 'Attachments', value: attachmentDisplay(config.attachments) },
    {
      label: 'Additional Info',
      value: config.additional_info?.provided ? (config.additional_info.preview || 'Provided') : '—',
    },
  ];
}

export default function RunConfigModal({
  show,
  onHide,
  runConfig,
  fallbackBaseUrl,
  runName,
  jobId,
}: RunConfigModalProps): React.ReactElement {
  const rows = runConfig ? buildRows(runConfig, fallbackBaseUrl, runName) : [];
  const title = runConfig?.run_name || runName || 'Execution details';
  const sourceBadge = sourceRunBadge(runConfig);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable className="run-config-modal">
      <Modal.Header closeButton>
        <div className="run-config-modal__title-wrap">
          <Modal.Title className="run-config-modal__title">Run Configuration</Modal.Title>
          <div className="run-config-modal__subtitle">
            {title}
            {jobId && <span className="run-config-modal__job">Job #{jobId}</span>}
            {sourceBadge && (
              <span className="run-config-modal__source" title={sourceBadge}>
                {sourceBadge}
              </span>
            )}
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        {!runConfig ? (
          <div className="run-config-modal__empty">Run configuration is unavailable for this execution.</div>
        ) : (
          <div className="run-config-table-wrap">
            <table className="run-config-table" data-responsive-cards="true">
              <tbody>
                {rows.map((row, index) => {
                  if ('section' in row) {
                    return (
                      <tr key={`section-${row.section}-${index}`} className="run-config-table__section">
                      <td colSpan={2} data-label="">{row.section}</td>
                      </tr>
                    );
                  }
                  return (
                    <tr key={`${row.label}-${index}`}>
                      <th>{row.label}</th>
                      <td className={row.mono ? 'run-config-table__mono' : undefined} data-label={row.label}>{row.value}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}
